// advanceInRemark — catch money collected in a REMARK instead of the
// partial-COD field.
//
// Real case (DP07118): an agent wrote "Cust advanced 100 Rs paid, balance 721
// COD" as a free-text remark. payment.codAdvance was never set, so the courier
// would have been told to collect the full ₹675 and the customer would have
// paid that ₹100 twice. The remark's own arithmetic was wrong too — the order
// total was 675, not 721.
//
// A remark is not a payment record. This spots the pattern and says so loudly;
// it deliberately does NOT change any amount, because only the desk knows which
// number is true.

import { isCodOrder, codAdvance } from './codAmount';

const remarkText = (order) => {
    const r = order?.remark ?? order?.remarks;
    if (!r) return '';
    if (typeof r === 'string') return r;
    if (typeof r === 'object') {
        if (typeof r.text === 'string') return r.text;
        // A map of remark entries — read them all.
        return Object.values(r).map((x) => (typeof x === 'string' ? x : x?.text || '')).join(' ');
    }
    return '';
};

// "advanced 100", "adv 99 rs", "paid 150 advance", "100 rs paid"
const ADVANCE_WORDS = /\b(advance[ds]?|adv)\b/i;
const PAID_WORDS = /\b(paid|received|recd|collected)\b/i;

/** The first rupee figure in the text, if any. */
export const amountInText = (text) => {
    const m = String(text || '').match(/(?:rs\.?|₹)\s*(\d{1,6})|(\d{1,6})\s*(?:rs\.?|₹)/i);
    if (!m) return null;
    const n = Number(m[1] ?? m[2]);
    return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Does this order look like money was taken but never recorded?
 *
 * @returns {{suspect:boolean, amount:number|null, text:string, message:string}}
 */
export const advanceOnlyInRemark = (order) => {
    const none = { suspect: false, amount: null, text: '', message: '' };
    if (!isCodOrder(order)) return none;          // nothing for a courier to collect twice
    if (codAdvance(order) > 0) return none;       // properly recorded — fine

    const text = remarkText(order);
    if (!text) return none;
    // Needs BOTH an advance-ish word and a payment word, so "advance booking"
    // alone does not trip it.
    if (!(ADVANCE_WORDS.test(text) && PAID_WORDS.test(text))) return none;

    const amount = amountInText(text);
    const total = Math.round(Number(order?.payment?.total) || 0);
    return {
        suspect: true,
        amount,
        text: text.trim().slice(0, 160),
        message: amount
            ? `A remark says ₹${amount} was already collected, but no partial-COD advance is recorded. The courier will collect the full ₹${total} and the customer would pay that ₹${amount} twice. Set the advance properly, or correct the remark.`
            : `A remark mentions money already collected, but no partial-COD advance is recorded. The courier will collect the full ₹${total}. Set the advance properly, or correct the remark.`,
    };
};
